/**
 * Base URL for assets.
 * Can be configured via VITE_S3_BASE_URL environment variable.
 * Defaults to empty string, which serves assets from the public directory.
 */
export const S3_BASE_URL = import.meta.env.VITE_S3_BASE_URL || '';

/**
 * Helper to get the full URL for a splat file.
 * @param {string} path - The relative path to the splat file (e.g., '/splats/room.splat').
 * @returns {string} The full URL.
 */
export const getSplatUrl = (path) => {
  // If path already starts with http/https, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // Ensure path starts with / if it's relative
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Ensure S3_BASE_URL doesn't end with a slash if normalizedPath starts with one
  const baseUrl = S3_BASE_URL.endsWith('/') ? S3_BASE_URL.slice(0, -1) : S3_BASE_URL;
  return `${baseUrl}${normalizedPath}`;
};

/**
 * Helper to get the full URL for any asset file (images, videos, etc.).
 * @param {string} path - The relative path to the asset (e.g., '/images/image.jpg').
 * @returns {string} The full URL.
 */
export const getAssetUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // Clean path (remove leading . if present)
  let normalizedPath = path;
  if (normalizedPath.startsWith('./')) {
    normalizedPath = normalizedPath.substring(1);
  }

  // Automatically map old /assets/ paths to /images/ or /videos/ if they still exist in the code
  // This is a safety measure, but we should also update the components.
  if (normalizedPath.startsWith('/assets/')) {
    const filename = normalizedPath.split('/').pop();
    if (filename.endsWith('.mp4') || filename.endsWith('.webm') || filename.endsWith('.mov')) {
      normalizedPath = `/videos/${filename}`;
    } else {
      normalizedPath = `/images/${filename}`;
    }
  }

  if (!normalizedPath.startsWith('/')) {
    normalizedPath = `/${normalizedPath}`;
  }

  // Ensure S3_BASE_URL doesn't end with a slash if normalizedPath starts with one
  const baseUrl = S3_BASE_URL.endsWith('/') ? S3_BASE_URL.slice(0, -1) : S3_BASE_URL;
  return `${baseUrl}${normalizedPath}`;
};
